import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { BusinessMedia, UpdateBusinessMediaRequest } from "@/api/types";
import { sessionRevision } from "@/lib/session-request-scope";
import { useDashboardRequest } from "./dashboard-request";

export function useBusinessMedia() {
  const dashboard = useDashboardRequest();
  return useQuery({
    queryKey: dashboard.queryKey("media"),
    queryFn: ({ signal }) =>
      api.get("dashboard/media", { ...dashboard.requestOptions(), signal }).json<BusinessMedia>(),
    staleTime: 60_000,
  });
}

export function useUpdateBusinessMedia() {
  const dashboard = useDashboardRequest();
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    onMutate: () => sessionRevision(),
    mutationFn: (change: UpdateBusinessMediaRequest) => {
      const options = { ...dashboard.requestOptions(), retry: 0 };
      if (change.action === "upload_photo" || change.action === "upload_logo") {
        const body = new FormData();
        body.append("file", change.file);
        const path = change.action === "upload_photo" ? "photos" : "logo";
        return api(`dashboard/media/${path}`, {
          ...options,
          timeout: 120_000,
          method: change.action === "upload_logo" ? "PUT" : "POST",
          body,
        }).json<BusinessMedia>();
      }
      if (change.action === "order_photos") {
        return api
          .put("dashboard/media/photos/order", {
            ...options,
            json: { photo_ids: change.photo_ids },
          })
          .json<BusinessMedia>();
      }
      const path =
        change.action === "remove_photo"
          ? `photos/${encodeURIComponent(change.id)}`
          : change.action === "restore_gallery"
            ? "gallery"
            : "logo";
      return api.delete(`dashboard/media/${path}`, options).json<BusinessMedia>();
    },
    onSuccess: async (data, _change, revision) => {
      if (revision !== sessionRevision()) return;
      await queryClient.cancelQueries({ queryKey: dashboard.queryKey("media"), exact: true });
      if (revision !== sessionRevision()) return;
      queryClient.setQueryData(dashboard.queryKey("media"), data);
      invalidatePublicMedia(queryClient);
    },
    onError: (_error, _change, revision) => {
      if (revision !== sessionRevision()) return;
      void queryClient.invalidateQueries({ queryKey: dashboard.queryKey("media"), exact: true });
      invalidatePublicMedia(queryClient);
    },
  });
}

function invalidatePublicMedia(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ["businesses"] });
  void queryClient.invalidateQueries({ queryKey: ["search"] });
  void queryClient.invalidateQueries({ queryKey: ["bookings"] });
  void queryClient.invalidateQueries({ queryKey: ["booking"] });
  void queryClient.invalidateQueries({
    queryKey: ["dashboard"],
    predicate: (query) => query.queryKey.at(-1) !== "media",
  });
}
